/*
 * Marks subscription cart lines with a line item property so every renderer
 * can show it without being patched.
 *
 * The cart customers see is Rebuy's Smart Cart, whose markup carries no
 * subscription information — a subscription line and a one-time line of the
 * same variant are identical apart from price. Rather than inject a label into
 * Rebuy's DOM (fragile against its re-renders, and dead at the v2 cutover),
 * this attaches `Purchase type: Subscription` to the line at add time. Rebuy
 * renders line item properties natively, and so do checkout, order
 * confirmations, packing slips, and the admin.
 *
 * It hooks the cart API rather than the product forms because every widget on
 * the store — theme buy buttons, the Loop widget, Rebuy's own upsell adds —
 * converges on /cart/add. The property is only ever added at add time; the
 * cart is never mutated afterwards, because an unowned background cart writer
 * is exactly the FB-011 failure the Rebuy drop exists to remove.
 *
 * See docs/decisions.md 2026-08-05.
 */
(function () {
  'use strict';

  var KEY = 'Purchase type';
  var BASE = 'Subscription';

  /*
   * The line says how often it ships when the page that rendered the buy
   * control published the plan names (window.BP_PLAN_NAMES, set in the layout
   * from the product's selling plan groups). A surface that never rendered
   * them still gets the plain label, so this never shows less than before.
   */
  function labelFor(plan) {
    var names = window.BP_PLAN_NAMES;
    var name = names && plan != null ? names[String(plan)] : null;
    if (!name) return BASE;
    var freq = String(name).replace(/^deliver\s+/i, '').trim();
    return freq ? BASE + ', ' + freq : BASE;
  }
  var ADD_PATH = /\/cart\/add(\.js)?(\?|$)/;

  function isAddUrl(url) {
    if (!url) return false;
    try {
      return ADD_PATH.test(String(url));
    } catch (e) {
      return false;
    }
  }

  function hasPlan(value) {
    if (value === null || value === undefined) return false;
    var str = String(value).trim();
    return str !== '' && str !== 'one-time' && str !== 'onetime' && str !== '0';
  }

  /* ---- JSON payloads: {id, selling_plan} or {items:[...]} ---- */

  function markItem(item) {
    if (!item || typeof item !== 'object') return item;
    if (!hasPlan(item.selling_plan)) return item;
    var props = item.properties;
    if (props && typeof props === 'object' && Object.prototype.hasOwnProperty.call(props, KEY)) {
      return item;
    }
    if (!props || typeof props !== 'object') props = {};
    props[KEY] = labelFor(item.selling_plan);
    item.properties = props;
    return item;
  }

  function markPayload(payload) {
    if (!payload || typeof payload !== 'object') return payload;
    if (Array.isArray(payload.items)) {
      for (var i = 0; i < payload.items.length; i++) markItem(payload.items[i]);
      return payload;
    }
    return markItem(payload);
  }

  function markJsonBody(body) {
    var parsed;
    try {
      parsed = JSON.parse(body);
    } catch (e) {
      return null;
    }
    var before = JSON.stringify(parsed);
    var after = JSON.stringify(markPayload(parsed));
    return before === after ? null : after;
  }

  /* ---- FormData / urlencoded payloads ---- */

  function markFormData(form) {
    if (!hasPlan(form.get('selling_plan'))) return false;
    if (form.get('properties[' + KEY + ']')) return false;
    form.append('properties[' + KEY + ']', labelFor(form.get('selling_plan')));
    return true;
  }

  function markUrlEncoded(body) {
    var params;
    try {
      params = new URLSearchParams(body);
    } catch (e) {
      return null;
    }
    if (!hasPlan(params.get('selling_plan'))) return null;
    if (params.get('properties[' + KEY + ']')) return null;
    params.append('properties[' + KEY + ']', labelFor(params.get('selling_plan')));
    return params.toString();
  }

  /* ---- fetch ---- */

  /*
   * nativeFetch MUST be bound to window. A bare `fetch(...)` call from
   * strict-mode or module code — which is every modern bundle on this page,
   * Rebuy's included — arrives here with `this` undefined, and forwarding that
   * to the native function throws "Illegal invocation". That does not just
   * skip the label: it breaks the caller's request. It broke Rebuy's cart.
   */
  if (window.fetch) {
    var nativeFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : input && input.url;
        if (isAddUrl(url) && init && init.body) {
          if (typeof init.body === 'string') {
            var trimmed = init.body.trim();
            var rewritten =
              trimmed.charAt(0) === '{' ? markJsonBody(init.body) : markUrlEncoded(init.body);
            if (rewritten !== null) init.body = rewritten;
          } else if (typeof FormData !== 'undefined' && init.body instanceof FormData) {
            markFormData(init.body);
          }
        }
      } catch (e) {
        /* Never break an add-to-cart over a label. */
      }
      return nativeFetch.apply(null, arguments);
    };
  }

  /* ---- XMLHttpRequest ---- */

  if (window.XMLHttpRequest && XMLHttpRequest.prototype.send) {
    var nativeOpen = XMLHttpRequest.prototype.open;
    var nativeSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        this.__bpCartAdd = isAddUrl(url);
      } catch (e) {
        this.__bpCartAdd = false;
      }
      return nativeOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function (body) {
      try {
        if (this.__bpCartAdd && body) {
          if (typeof body === 'string') {
            var trimmed = body.trim();
            var rewritten =
              trimmed.charAt(0) === '{' ? markJsonBody(body) : markUrlEncoded(body);
            if (rewritten !== null) return nativeSend.call(this, rewritten);
          } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
            markFormData(body);
          }
        }
      } catch (e) {
        /* fall through to the unmodified send */
      }
      return nativeSend.apply(this, arguments);
    };
  }

  /* ---- native form POST (no JS interception in play) ---- */

  document.addEventListener(
    'submit',
    function (event) {
      try {
        var form = event.target;
        if (!form || form.tagName !== 'FORM') return;
        if (!isAddUrl(form.getAttribute('action'))) return;

        var plan = form.querySelector('[name="selling_plan"]');
        var existing = form.querySelector('[name="properties[' + KEY + ']"][data-bp-purchase-type]');

        /*
         * The field is rewritten on every submit, not created once. A shopper
         * who adds a second pack from the same page load would otherwise carry
         * the first add's wording onto the second line: three bottles added
         * after one bottle used to arrive labelled every four weeks. And a
         * line switched to one-time has to lose the label entirely rather than
         * keep claiming a subscription.
         */
        if (!plan || !hasPlan(plan.value)) {
          if (existing) existing.parentNode.removeChild(existing);
          return;
        }
        if (existing) {
          existing.value = labelFor(plan.value);
          return;
        }
        if (form.querySelector('[name="properties[' + KEY + ']"]')) return;

        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'properties[' + KEY + ']';
        hidden.setAttribute('data-bp-purchase-type', '');
        hidden.value = labelFor(plan.value);
        form.appendChild(hidden);
      } catch (e) {
        /* leave the submit untouched */
      }
    },
    true
  );
})();
